import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Snackbar,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Link as MuiLink,
  InputAdornment,
} from '@mui/material';
import {
  TrendingUp as TrackerIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  AddComment as LogContactIcon,
  PersonAdd as AssignIcon,
  CheckCircle as SignedIcon,
  RemoveCircleOutline as RemoveIcon,
  RequestQuote as QuoteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';
import MarkdownContent from '../Insights/MarkdownContent';
import { normalizeConfidence, normalizeMethod } from '../Insights/insightUtils';

// Follow-up status thresholds (legacy GetSatusNumber): overdue = red,
// less than 8 hours remaining = yellow, otherwise green.
const DUE_SOON_HOURS = 8;

const getFollowUpStatus = (followUpAt) => {
  if (!followUpAt) return { key: 'none', minutes: null };
  const remainingMs = new Date(followUpAt).getTime() - Date.now();
  const minutes = Math.round(remainingMs / 60000);
  if (minutes <= 0) return { key: 'overdue', minutes: Math.abs(minutes) };
  if (minutes < DUE_SOON_HOURS * 60) return { key: 'dueSoon', minutes };
  return { key: 'onTrack', minutes };
};

const STATUS_CHIP_COLOR = { overdue: 'error', dueSoon: 'warning', onTrack: 'success', none: 'default' };
const PRIORITY_CHIP_COLOR = {
  critical: 'error',
  urgent: 'error',
  high: 'error',
  medium: 'warning',
  low: 'success',
    normal: 'default',
};

const getExplainablePriority = (row) => {
  const raw = row.explainable_priority ?? row.explainablePriority ?? row.priority;
  const hasFlatPriority = [
    row.priority_level,
    row.priorityLevel,
    row.priority_score,
    row.priorityScore,
    row.priority_reason,
    row.priorityReason,
  ].some((value) => value !== undefined && value !== null && value !== '');

  if ((raw === undefined || raw === null || raw === '') && !hasFlatPriority) return null;

  const data = typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const level = data.level ?? data.band ?? data.label ?? row.priority_level ?? row.priorityLevel
    ?? (typeof raw === 'string' ? raw : '');
  const scoreValue = data.score ?? data.value ?? row.priority_score ?? row.priorityScore
    ?? (typeof raw === 'number' ? raw : null);
  const confidence = normalizeConfidence(data.confidence ?? row.priority_confidence ?? row.priorityConfidence);
  const factors = data.factors ?? data.reasons ?? row.priority_factors ?? row.priorityFactors;
  const reason = data.reason ?? data.explanation ?? row.priority_reason ?? row.priorityReason
    ?? (Array.isArray(factors) ? factors.join('\n- ') : '');
  const methodValue = data.method ?? row.priority_method ?? row.priorityMethod;

  return {
    level: String(level || '').toLowerCase(),
    score: scoreValue === undefined || scoreValue === null || scoreValue === '' ? null : Number(scoreValue),
    reason: String(reason || ''),
    factors: factors && typeof factors === 'object' && !Array.isArray(factors) ? factors : null,
    method: methodValue ? normalizeMethod(methodValue) : '',
    confidence,
  };
};

// Default next follow-up: tomorrow at 9:00 local, as datetime-local string.
const defaultFollowUpLocal = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const SalesTracker = ({ onNavigateToCustomer }) => {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || 'en').startsWith('es') ? 'es-MX' : 'en-US';

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ quoted: 0, nonQuoted: 0 });
  const [salesPersons, setSalesPersons] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [salesPersonFilter, setSalesPersonFilter] = useState('');

  // Dialog state: { type: 'log'|'assign'|'signed'|'remove', row }
  const [dialog, setDialog] = useState(null);
  const [dialogNotes, setDialogNotes] = useState('');
  const [dialogFollowUp, setDialogFollowUp] = useState(defaultFollowUpLocal());
  const [dialogSalesPersonId, setDialogSalesPersonId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [res, priorityRes] = await Promise.all([
        makeApiCall('api/sales-tracker'),
        makeApiCall('api/insights/sales-priorities').catch(() => null),
      ]);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || t('salesTracker.loadFailed'));
      }
      const data = await res.json();
      const priorityData = priorityRes?.ok
        ? await priorityRes.json().catch(() => ({}))
        : {};
      const priorityByQuote = new Map(
        (priorityData.priorities || []).map((priority) => [String(priority.quote_id), priority])
      );
      const mergedRows = (data.rows || []).map((row) => {
        if (row.explainable_priority || row.explainablePriority || !row.quote_id) return row;
        const priority = priorityByQuote.get(String(row.quote_id));
        if (!priority) return row;
        return {
          ...row,
          explainable_priority: {
            level: priority.priority,
            method: priorityData.source_type,
            reason: t('salesTracker.priorityReason', { count: priority.days_since_update }),
          },
        };
      });
      setRows(mergedRows);
      setTotals(data.totals || { quoted: 0, nonQuoted: 0 });
      setSalesPersons(data.salesPersons || []);
      setError('');
    } catch (err) {
      setError(err.message || t('salesTracker.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (groupFilter !== 'all' && row.group !== groupFilter) return false;
      if (salesPersonFilter && String(row.assigned_sales_person_id || '') !== String(salesPersonFilter)) return false;
      if (!q) return true;
      const haystack = [
        row.first_name, row.last_name, row.company_name,
        row.city, row.street_address, row.quote_number,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, search, groupFilter, salesPersonFilter]);

  const overdueCount = useMemo(
    () => rows.filter((r) => getFollowUpStatus(r.follow_up_at).key === 'overdue').length,
    [rows]
  );

  const contactName = (row) =>
    [row.first_name, row.last_name].filter(Boolean).join(' ')
    || row.company_name
    || t('salesTracker.unnamedContact');

  const formatRemaining = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h <= 0) return t('salesTracker.minutesShort', { count: m });
    return t('salesTracker.hoursMinutesShort', { hours: h, minutes: m });
  };

  const statusChip = (row) => {
    const status = getFollowUpStatus(row.follow_up_at);
    let label;
    if (status.key === 'overdue') label = t('salesTracker.overdueBy', { time: formatRemaining(status.minutes) });
    else if (status.key === 'dueSoon') label = t('salesTracker.dueIn', { time: formatRemaining(status.minutes) });
    else if (status.key === 'onTrack') {
      label = t('salesTracker.followUpOn', {
        date: new Date(row.follow_up_at).toLocaleDateString(locale, { month: 'short', day: 'numeric' }),
      });
    } else label = t('salesTracker.noFollowUp');
    return (
      <Chip
        size="small"
        color={STATUS_CHIP_COLOR[status.key]}
        label={label}
        aria-label={t('salesTracker.followUpStatusAria', { status: label })}
      />
    );
  };

  const priorityDisplay = (row) => {
    const priority = getExplainablePriority(row);
    if (!priority) return '—';
    const score = Number.isFinite(priority.score)
      ? (priority.score <= 1 ? Math.round(priority.score * 100) : Math.round(priority.score))
      : null;
    const translatedLevel = priority.level
      ? t(`insights.priorities.${priority.level}`, { defaultValue: priority.level })
      : t('salesTracker.priority');
    const label = score === null
      ? translatedLevel
      : t('salesTracker.priorityWithScore', { priority: translatedLevel, score });

    return (
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center">
          <Chip
            size="small"
            color={PRIORITY_CHIP_COLOR[priority.level] || 'default'}
            label={label}
            aria-label={t('salesTracker.priorityAria', { priority: label })}
          />
          {priority.method && (
            <Chip
              size="small"
              variant="outlined"
              label={t(`insights.methods.${priority.method}`)}
              aria-label={t('insights.methodAria', { method: t(`insights.methods.${priority.method}`) })}
            />
          )}
          {priority.confidence !== null && (
            <Typography variant="caption" color="text.secondary">
              {t('insights.confidenceValue', { confidence: Math.round(priority.confidence) })}
            </Typography>
          )}
        </Stack>
        {(priority.reason || priority.factors) && (
          <MarkdownContent color="text.secondary" sx={{ mt: 0.5 }}>
            {priority.reason || t(
              Number(priority.factors?.hours_until_follow_up) < 0
                ? 'salesTracker.priorityFactorsOverdue'
                : 'salesTracker.priorityFactors',
              {
              hours: priority.factors?.hours_until_follow_up === null
                ? t('insights.notAvailable')
                : Math.abs(priority.factors?.hours_until_follow_up),
              value: Number(priority.factors?.quote_value || 0).toLocaleString(locale, {
                style: 'currency',
                currency: 'CAD',
                maximumFractionDigits: 0,
              }),
            })}
          </MarkdownContent>
        )}
      </Box>
    );
  };

  const openDialog = (type, row) => {
    setDialog({ type, row });
    setDialogNotes('');
    setDialogFollowUp(defaultFollowUpLocal());
    setDialogSalesPersonId(row.assigned_sales_person_id || '');
  };

  const closeDialog = () => {
    if (!submitting) setDialog(null);
  };

  const submitDialog = async () => {
    if (!dialog) return;
    const { type, row } = dialog;
    setSubmitting(true);
    try {
      let endpoint;
      let body;
      if (type === 'log') {
        endpoint = 'api/sales-tracker/events';
        body = {
          customer_id: row.customer_id,
          quote_id: row.quote_id || null,
          notes: dialogNotes.trim() || null,
          follow_up_at: new Date(dialogFollowUp).toISOString(),
        };
      } else if (type === 'assign') {
        endpoint = 'api/sales-tracker/assign';
        body = { customer_id: row.customer_id, sales_person_id: dialogSalesPersonId || null };
      } else if (type === 'signed') {
        endpoint = 'api/sales-tracker/mark-signed';
        body = { customer_id: row.customer_id, quote_id: row.quote_id || null };
      } else {
        endpoint = 'api/sales-tracker/remove';
        body = { customer_id: row.customer_id, quote_id: row.quote_id || null, notes: dialogNotes.trim() || null };
      }

      const res = await makeApiCall(endpoint, { method: 'POST', body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || t('salesTracker.actionFailed'));
      }
      setSuccess(t(`salesTracker.${type}Success`));
      setDialog(null);
      await loadData();
    } catch (err) {
      setError(err.message || t('salesTracker.actionFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderActions = (row) => (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
      <Tooltip title={t('salesTracker.logContact')}>
        <IconButton size="small" color="primary" onClick={() => openDialog('log', row)} aria-label={t('salesTracker.logContact')}>
          <LogContactIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('salesTracker.assignSalesPerson')}>
        <IconButton size="small" onClick={() => openDialog('assign', row)} aria-label={t('salesTracker.assignSalesPerson')}>
          <AssignIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('salesTracker.markSigned')}>
        <IconButton size="small" color="success" onClick={() => openDialog('signed', row)} aria-label={t('salesTracker.markSigned')}>
          <SignedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('salesTracker.removeFromTracker')}>
        <IconButton size="small" color="error" onClick={() => openDialog('remove', row)} aria-label={t('salesTracker.removeFromTracker')}>
          <RemoveIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const rowKey = (row) => `${row.group}-${row.customer_id}-${row.quote_id || 0}`;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <TrackerIcon sx={{ color: '#00897b' }} fontSize="large" />
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          {t('salesTracker.title')}
        </Typography>
        <Tooltip title={t('common.refresh')}>
          <IconButton onClick={loadData} aria-label={t('common.refresh')}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Stats */}
      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
        <Chip color="primary" label={t('salesTracker.quotedCount', { count: totals.quoted })} />
        <Chip color="default" label={t('salesTracker.nonQuotedCount', { count: totals.nonQuoted })} />
        <Chip color="error" variant={overdueCount ? 'filled' : 'outlined'} label={t('salesTracker.overdueCount', { count: overdueCount })} />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('salesTracker.searchPlaceholder')}
              aria-label={t('salesTracker.searchPlaceholder')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              size="small"
              sx={{ minWidth: 180 }}
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              label={t('salesTracker.groupFilter')}
              aria-label={t('salesTracker.groupFilter')}
            >
              <MenuItem value="all">{t('salesTracker.groupAll')}</MenuItem>
              <MenuItem value="quoted">{t('salesTracker.quoted')}</MenuItem>
              <MenuItem value="non_quoted">{t('salesTracker.nonQuoted')}</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              sx={{ minWidth: 200 }}
              value={salesPersonFilter}
              onChange={(e) => setSalesPersonFilter(e.target.value)}
              label={t('salesTracker.salesPerson')}
              aria-label={t('salesTracker.salesPerson')}
            >
              <MenuItem value="">{t('salesTracker.allSalesPersons')}</MenuItem>
              {salesPersons.map((sp) => (
                <MenuItem key={sp.id} value={sp.id}>{sp.display_name}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </CardContent>
      </Card>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && filteredRows.length === 0 && (
        <Alert severity="info">{t('salesTracker.empty')}</Alert>
      )}

      {/* Desktop table */}
      {!loading && filteredRows.length > 0 && (
        <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Table size="small" aria-label={t('salesTracker.title')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('salesTracker.contact')}</TableCell>
                <TableCell>{t('salesTracker.companyCity')}</TableCell>
                <TableCell>{t('salesTracker.salesPerson')}</TableCell>
                <TableCell>{t('salesTracker.lastContact')}</TableCell>
                <TableCell>{t('salesTracker.quote')}</TableCell>
                <TableCell>{t('salesTracker.status')}</TableCell>
                <TableCell>{t('salesTracker.priority')}</TableCell>
                <TableCell align="right">{t('salesTracker.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row) => (
                <TableRow key={rowKey(row)} hover>
                  <TableCell>
                    <MuiLink
                      component="button"
                      type="button"
                      onClick={() => onNavigateToCustomer && onNavigateToCustomer(row.customer_id)}
                      sx={{ textAlign: 'left' }}
                      aria-label={t('salesTracker.openCustomerAria', { name: contactName(row) })}
                    >
                      {contactName(row)}
                    </MuiLink>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {row.group === 'quoted' ? t('salesTracker.quoted') : t('salesTracker.nonQuoted')}
                      {row.event_counter ? ` · ${t('salesTracker.contactNumber', { count: row.event_counter })}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.company_name && (
                      <Typography variant="body2">{row.company_name}</Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {[row.street_address, row.city].filter(Boolean).join(', ')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.sales_person_name || (
                      <Typography variant="caption" color="text.secondary">
                        {t('salesTracker.unassigned')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.anchor_date
                      ? new Date(row.anchor_date).toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' })
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {row.quote_id ? (
                      <Stack spacing={0}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <QuoteIcon fontSize="inherit" sx={{ color: '#de6127' }} />
                          <Typography variant="body2">{row.quote_number || `#${row.quote_id}`}</Typography>
                        </Stack>
                        {row.total_amount != null && (
                          <Typography variant="caption" color="text.secondary">
                            {Number(row.total_amount).toLocaleString(locale, { style: 'currency', currency: 'CAD' })}
                          </Typography>
                        )}
                      </Stack>
                    ) : '—'}
                  </TableCell>
                  <TableCell>{statusChip(row)}</TableCell>
                  <TableCell sx={{ minWidth: 210 }}>{priorityDisplay(row)}</TableCell>
                  <TableCell align="right">{renderActions(row)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Mobile cards */}
      {!loading && filteredRows.length > 0 && (
        <Stack spacing={1.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
          {filteredRows.map((row) => (
            <Card key={rowKey(row)} variant="outlined">
              <CardContent sx={{ pb: 1, '&:last-child': { pb: 1 } }}>
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <MuiLink
                      component="button"
                      type="button"
                      onClick={() => onNavigateToCustomer && onNavigateToCustomer(row.customer_id)}
                      sx={{ textAlign: 'left', fontWeight: 600 }}
                      aria-label={t('salesTracker.openCustomerAria', { name: contactName(row) })}
                    >
                      {contactName(row)}
                    </MuiLink>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {[row.company_name, row.city].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                  {statusChip(row)}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={row.group === 'quoted'
                      ? (row.quote_number || t('salesTracker.quoted'))
                      : t('salesTracker.nonQuoted')}
                  />
                  {row.sales_person_name && (
                    <Chip size="small" variant="outlined" label={row.sales_person_name} />
                  )}
                </Stack>
                {getExplainablePriority(row) && (
                  <Box sx={{ mt: 1 }}>
                    {priorityDisplay(row)}
                  </Box>
                )}
                {renderActions(row)}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Action dialog */}
      <Dialog open={!!dialog} onClose={closeDialog} fullWidth maxWidth="xs" aria-labelledby="sales-tracker-dialog-title">
        <DialogTitle id="sales-tracker-dialog-title">
          {dialog?.type === 'log' && t('salesTracker.logContact')}
          {dialog?.type === 'assign' && t('salesTracker.assignSalesPerson')}
          {dialog?.type === 'signed' && t('salesTracker.markSigned')}
          {dialog?.type === 'remove' && t('salesTracker.removeFromTracker')}
        </DialogTitle>
        <DialogContent>
          {dialog && (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {contactName(dialog.row)}
                {dialog.row.quote_number ? ` · ${dialog.row.quote_number}` : ''}
              </Typography>

              {dialog.type === 'log' && (
                <>
                  <TextField
                    label={t('salesTracker.eventNotes')}
                    value={dialogNotes}
                    onChange={(e) => setDialogNotes(e.target.value)}
                    multiline
                    minRows={3}
                    fullWidth
                    aria-label={t('salesTracker.eventNotes')}
                  />
                  <TextField
                    label={t('salesTracker.nextFollowUp')}
                    type="datetime-local"
                    value={dialogFollowUp}
                    onChange={(e) => setDialogFollowUp(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    aria-label={t('salesTracker.nextFollowUp')}
                  />
                </>
              )}

              {dialog.type === 'assign' && (
                <TextField
                  select
                  label={t('salesTracker.salesPerson')}
                  value={dialogSalesPersonId}
                  onChange={(e) => setDialogSalesPersonId(e.target.value)}
                  fullWidth
                  aria-label={t('salesTracker.salesPerson')}
                >
                  <MenuItem value="">{t('salesTracker.unassigned')}</MenuItem>
                  {salesPersons.map((sp) => (
                    <MenuItem key={sp.id} value={sp.id}>{sp.display_name}</MenuItem>
                  ))}
                </TextField>
              )}

              {dialog.type === 'signed' && (
                <Typography variant="body2">{t('salesTracker.markSignedConfirm')}</Typography>
              )}

              {dialog.type === 'remove' && (
                <>
                  <Typography variant="body2">{t('salesTracker.removeConfirm')}</Typography>
                  <TextField
                    label={t('salesTracker.removalReason')}
                    value={dialogNotes}
                    onChange={(e) => setDialogNotes(e.target.value)}
                    multiline
                    minRows={2}
                    fullWidth
                    aria-label={t('salesTracker.removalReason')}
                  />
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color={dialog?.type === 'remove' ? 'error' : 'primary'}
            onClick={submitDialog}
            disabled={submitting || (dialog?.type === 'log' && !dialogFollowUp)}
            startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
            aria-label={t('common.confirm')}
          >
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
        message={success}
      />
    </Box>
  );
};

export default SalesTracker;
